import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { MediumHeader } from '@narvar/nth-header';
import Button from '@narvar/nth-button';
import styled from 'styled-components';
import { formatCurrencyValue } from '../../../lib/currencyFormatters';
import Loader from '../../Loader';
import StripeFooter from '../../StripeWidget/components/StripeFooter';

export const mapStateToProps = ({
  language = {},
  page: { step2Complete, locale },
  retailerSettings: {
    branding: { brand_call_to_action: ctaColor, brand_call_to_action_hover: ctaHoverColor } = {},
  } = {},
  stripe: { service, fee },
}) => ({
  language,
  fee,
  ctaColor,
  ctaHoverColor,
  service,
  disabled: !step2Complete,
  locale,
});

const StyledInputWrapper = styled.div`
  display: inline-block;
  border: 1px solid #000000;
  padding-left: 0.5rem;
  @media only screen and (max-width: 768px) {
    padding-left: 0.25rem;
  }
  height: 40px;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  > div {
    width: 100%;
  }
`;

const StyledNumber = styled(StyledInputWrapper)`
  width: 60%;
`;

const StyledContainer = styled(StyledInputWrapper)`
  width: 20%;
`;

const StyledErrors = styled.div`
  margin: 1rem 0;
  color: #eb1c26;
  text-align: center;
`;

const CardCapture = ({
  service,
  featureName,
  fee,
  paymentIntentSecret,
  onSuccess,
  language,
  ctaColor,
  ctaHoverColor,
  disabled,
  locale,
}) => {
  const [validationError, setValidationError] = useState('');
  const [serverError, setServerError] = useState('');
  const [confirmingCard, setConfirmingCard] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  // Handle real-time validation errors from the card Element.
  const handleChange = (event) => {
    if (event.error) {
      setValidationError(event.error.message);
    } else {
      setValidationError('');
    }
  };

  // POST the token ID to your backend.
  const confirmPayment = async () => {
    if (!elements) return;
    const cardElement = elements.getElement(CardNumberElement);
    if (!stripe || !cardElement) return;
    setConfirmingCard(true);
    try {
      const result = await stripe.confirmCardPayment(paymentIntentSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (result.error && result.error.message) {
        setServerError(result.error.message);
      } else {
        setServerError('');
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
        // The payment has succeeded
        // Display a success message
      }
      setConfirmingCard(false);
    } catch (e) {
      setConfirmingCard(false);
      setServerError('Something went wrong');
    }
  };

  return (
    <div data-test="stripe-card-capture">
      <div style={{ textAlign: 'center' }}>
        <MediumHeader>
          {(
            language[`returns.stripe_title.${service}`] || language[`returns.stripe_title`]
          ).replace(
            '{fee}',
            formatCurrencyValue({
              value: fee,
              locale,
            }),
          )}
        </MediumHeader>
        <span
          style={{
            color: '#7A7A7A',
            marginBottom: '1.5rem',
            display: 'block',
          }}
        >
          {(
            language[`returns.stripe_subtitle.${service}`] || language[`returns.stripe_subtitle`]
          ).replace(
            '{fee}',
            formatCurrencyValue({
              value: fee,
              locale,
            }),
          )}
        </span>
      </div>
      <div className="form-row">
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label
          id="stripe-payment-label"
          htmlFor="card-element"
          style={{
            display: 'block',
            textAlign: 'center',
          }}
        >
          {language[`returns.stripe_card_title.${service}`] ||
            language[`returns.stripe_card_title`]}
        </label>
        <div className="fs-exclude">
          <StyledNumber>
            <CardNumberElement
              id="stripe-card-number-element"
              options={{
                showIcon: true,
                placeholder:
                  language[`returns.stripe_card_number_placeholder.${service}`] ||
                  language[`returns.stripe_card_number_placeholder`],
              }}
              className="stripe-card-number stripe-element"
              onChange={handleChange}
            />
          </StyledNumber>
          <StyledContainer>
            <CardExpiryElement
              id="stripe-card-expiry-element"
              options={{
                placeholder:
                  language[`returns.stripe_card_expiry_placeholder.${service}`] ||
                  language[`returns.stripe_card_expiry_placeholder`],
              }}
              className="stripe-card-expiry stripe-element"
              onChange={handleChange}
            />
          </StyledContainer>
          <StyledContainer>
            <CardCvcElement
              id="stripe-card-cvc-element"
              options={{
                placeholder:
                  language[`returns.stripe_card_cvc_placeholder.${service}`] ||
                  language[`returns.stripe_card_cvc_placeholder`],
              }}
              className="stripe-card-cvc stripe-element"
              onChange={handleChange}
            />
          </StyledContainer>
        </div>
        <StyledErrors className="card-errors" role="alert">
          {validationError || serverError}
        </StyledErrors>
      </div>
      {confirmingCard ? (
        <Loader />
      ) : (
        <Button
          disabled={Boolean(validationError) || disabled}
          className="narvar-btn-primary"
          big
          featureName={featureName}
          onClick={confirmPayment}
          s={{
            height: '2.5rem',
            backgroundColor: ctaColor,
            hover: {
              backgroundColor: ctaHoverColor,
            },
          }}
        >
          {language[`returns.stripe_card_submit_button.${service}`] ||
            language[`returns.stripe_card_submit_button`]}
        </Button>
      )}

      <div style={{ marginTop: '1rem' }}>
        <StripeFooter id={service} />
      </div>
    </div>
  );
};

CardCapture.defaultProps = {
  service: undefined,
  featureName: 'stripePayment',
  onSuccess: () => {},
  linkColor: undefined,
  linkHoverColor: undefined,
  ctaColor: undefined,
  ctaHoverColor: undefined,
  disabled: false,
};

CardCapture.propTypes = {
  service: PropTypes.string,
  languageCode: PropTypes.string.isRequired,
  featureName: PropTypes.string,
  paymentIntentSecret: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  language: PropTypes.object.isRequired,
  linkColor: PropTypes.string,
  linkHoverColor: PropTypes.string,
  ctaColor: PropTypes.string,
  ctaHoverColor: PropTypes.string,
  fee: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  locale: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(CardCapture);
